import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { SlideConfig, SlideKey } from '../config/slides';

gsap.registerPlugin(ScrollToPlugin);

interface NavigationContextType {
    activeSlideIndex: number;
    activeSlideKey: SlideKey;
    setActiveSlideIndex: (index: number) => void;
    slides: SlideConfig[];
    scrollToIndex: (index: number) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('useNavigation must be used within a NavigationProvider');
    }
    return context;
};

interface NavigationProviderProps {
    children: ReactNode;
    slides: SlideConfig[];
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children, slides }) => {
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    const activeSlideKey = slides[activeSlideIndex]?.key || "home";

    const scrollToIndex = useCallback((index: number) => {
        const container = document.querySelector('.ms-container') as HTMLElement | null;
        if (!container) return;

        const target = container.children[index] as HTMLElement | undefined;
        if (!target) return;

        // Inform MegaScroll that a programmatic scroll is in progress
        // so it can suppress intermediate onChange events
        container.dataset.targetIndex = String(index);

        // Kill any in-flight tweens on the container to avoid fighting
        gsap.killTweensOf(container);
        gsap.to(container, {
            scrollTo: { y: target, offsetY: 0 },
            duration: 0.8,
            ease: "power2.inOut",
            overwrite: true,
            onComplete: () => {
                container.removeAttribute('data-target-index');
            },
        });
    }, []);

    const handleHashChange = useCallback(() => {
        const currentHash = window.location.hash;
        const targetSlide = slides.find(s => s.hash === currentHash);

        if (targetSlide && targetSlide.elementId) {
            const currentIndex = slides.findIndex(s => s.key === targetSlide.key);
            if (currentIndex >= 0) {
                scrollToIndex(currentIndex);
            }
        }
    }, [slides, scrollToIndex]);

    useEffect(() => {
        window.addEventListener("hashchange", handleHashChange);
        const timer = setTimeout(handleHashChange, 100);

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
            clearTimeout(timer);
        };
    }, [handleHashChange]);

    const value = useMemo(() => ({
        activeSlideIndex,
        activeSlideKey,
        setActiveSlideIndex,
        slides,
        scrollToIndex,
    }), [activeSlideIndex, activeSlideKey, setActiveSlideIndex, slides, scrollToIndex]);

    return (
        <NavigationContext.Provider value={value}>
            {children}
        </NavigationContext.Provider>
    );
};
